import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Delete as ClearIcon
} from '@mui/icons-material';
import { useApiClient } from '../../config/axios';
import ChatBubble from './ChatBubble';
import TypingIndicator from './TypingIndicator';
import { ChatMessage, ChatContext } from './types';

interface ChatbotPanelProps {
  currentView?: string;
  currentViewId?: string;
  onClose?: () => void;
}

const ChatbotPanel: React.FC<ChatbotPanelProps> = ({
  currentView,
  currentViewId,
  onClose
}) => {
  const [input, setInput] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const apiClient = useApiClient();

  // Generate session ID on mount
  useEffect(() => {
    const storedSessionId = localStorage.getItem('chatSessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      // Load chat history from storage
      const storedHistory = localStorage.getItem('chatHistory');
      if (storedHistory) {
        try {
          const parsed = JSON.parse(storedHistory);
          // Convert timestamp strings back to Date objects
          const restored = parsed.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          setChatHistory(restored);
        } catch (e) {
          console.error('Failed to load chat history:', e);
        }
      }
    } else {
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      localStorage.setItem('chatSessionId', newSessionId);
    }
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, isTyping]);

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    if (chatHistory.length > 0) {
      localStorage.setItem('chatHistory', JSON.stringify(chatHistory));
    }
  }, [chatHistory]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isTyping) return;

    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    // Add user message to history
    setChatHistory(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Build context
      const context: ChatContext = {
        currentView,
        currentViewId,
        conversationHistory: chatHistory.slice(-5) // Last 5 messages for context
      };

      // Send to backend
      const response = await apiClient.post('/mass-update/chat', {
        prompt: userMessage.content,
        context,
        sessionId
      });

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: response.data.message || response.data.reasoning || 'I processed your request.',
        timestamp: new Date(),
        results: response.data.results,
        resultType: response.data.resultType,
        reasoning: response.data.reasoning
      };

      // Add assistant message to history
      setChatHistory(prev => [...prev, assistantMessage]);

    } catch (error: any) {
      console.error('Chat error:', error);

      // Add error message
      const errorMessage: ChatMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.response?.data?.error || error.message || 'Unknown error'}. Please try again.`,
        timestamp: new Date()
      };

      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  }, [apiClient, chatHistory, currentView, currentViewId, input, isTyping, sessionId]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      setChatHistory([]);
      localStorage.removeItem('chatHistory');
      // Generate new session ID
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      localStorage.setItem('chatSessionId', newSessionId);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography variant="h6">AI Assistant</Typography>
          {currentView && (
            <Typography variant="caption" color="text.secondary">
              Context: {currentView}
            </Typography>
          )}
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={handleClearHistory}
            title="Clear History"
            disabled={chatHistory.length === 0}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
          {onClose && (
            <IconButton size="small" onClick={onClose} title="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Chat Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: 'grey.50'
        }}
      >
        {chatHistory.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Welcome to AI Assistant
            </Typography>
            <Typography variant="body2">
              Ask me anything about {currentView || 'your views'}!
            </Typography>
            <Typography variant="caption" sx={{ mt: 2 }}>
              Try asking:
            </Typography>
            <Typography variant="caption">
              • "Show formulas in this view"
            </Typography>
            <Typography variant="caption">
              • "Find duplicate formulas"
            </Typography>
            <Typography variant="caption">
              • "Which views use 'Sales Units'?"
            </Typography>
          </Box>
        ) : (
          <>
            {chatHistory.map(message => (
              <ChatBubble key={message.id} message={message} />
            ))}
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      <Divider />

      {/* Input Area */}
      <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={currentView ? `Ask about ${currentView}...` : "Ask me anything..."}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isTyping}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={handleSend}
                disabled={!input.trim() || isTyping}
                color="primary"
              >
                <SendIcon />
              </IconButton>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Press Enter to send • Shift+Enter for new line
        </Typography>
      </Box>
    </Box>
  );
};

export default ChatbotPanel;
